import React, { useRef, useMemo, useState, useEffect } from "react";

import { Alert } from "services/Alert";
import { AlertBridge } from "services/AlertBridge";
import { Route, Switch, Redirect } from "react-router-dom";
import Recaptcha from "react-google-invisible-recaptcha";
import { useLocation } from "react-router-dom";
import { IntlProvider } from "react-intl";
import { connect } from "react-redux"
import styled, { ThemeProvider } from "styled-components";

import { getReferences, getDialerTrunk, setInitedReferences } from "actions/references";
import { LOCAL_STORAGE_DELAYED_SERVICE_CHANGE } from "const/localStorageKeys";
import { getPermission, setService, checkCaptchaNeeded } from "actions/auth";
import { getAccountManagersInformators } from "actions/account_managers";
import { ACCOUNT_MANAGERS_GET_INFORMATORS_API } from "const/apiMethods";
import { useLastVisitedTracker } from "hooks/useLastVisitedTracker";
import useAuthRedirectManager from "hooks/useAuthRedirectManager";
import { storeDataOnRecaptchaComplete } from "actions/settings";
import { setBodyClassNames, getManagedServices } from "utils";
import { getAccountDialerInfo } from "actions/auth_dialer";
import { setDialerService } from "actions/auth_dialer";
import { api } from "api/loginRoutes";
import {
    APP_TYPE_ADMIN,
    APP_TYPE_CLIENT,
    APP_TYPE_DEFAULT,
    HAS_BOTH_SERVICE_VALUE,
    DEFAULT_NOT_AUTH_PATH,
    transformed_service_data
} from "const";
import ModalResponseHandlerGlobalHOC from "components/Modal/ModalResponseHandlerGlobalHOC";
import DebugFooterOverlay from "components/debug/DebugFooterOverlay";
import CacheBooster from "components/CacheBooster";
import themeContext from "context/themeContext";
import ClientRoutes from "routes/RoutesClient";
import { CustomModal } from "components/base";
import AdminRoutes from "routes/RoutesAdmin";
import usePrevious from "hooks/usePrevious";
import localesRsuite from "locales-rsuite";
import store from "store/index";
import locales from "locales";
import PaymentDetailConfirm from "pagesClient/PaymentDetails/PaymentDetailConfirm";
import EmailVerification from "pages/EmailVerification/EmailVerification";
import ResetPassword from "pages/ResetPassword/ResetPassword";
import Maintenance from "pages/Maintenance/Maintenance";
import Login from "pages/Login/Login";

import { Button, CustomProvider, Form } from "rsuite";

export const AppTypeContext = React.createContext(APP_TYPE_DEFAULT);
export const RtlContext = React.createContext(false);
export const PermissionContext = React.createContext([]);


function App(
    {
        auth,
        authInfo,
        lang,
        rtl,
        service,
        setService,
        permissions,
        role_list,
        appType,

        referenceInited,
        // smsNotDeployed,
        // voiceNotDeployed,

        dialerInfo,
        accountJoinList,
        methodRecaptcha,
        paramsRecaptcha,
        visibleRecaptcha,

        getReferences,
        getPermission,
        maintenanceMode,
        serviceDialer,
        getDialerTrunk,
        setDialerService,
        checkCaptchaNeeded,
        getAccountDialerInfo,
        getAccountManagersInformators
    }
) {
    const location = useLocation();
    const safePermissions = useMemo(
        () => Array.isArray(permissions) ? permissions : [],
        [permissions]
    );

    const recaptchaRef = useRef(null);
    // const hiddenFormRef = useRef(null);
    const [recaptchaIsVisible, setRecaptchaIsVisible] = useState(visibleRecaptcha);
    const [showRecaptchaModal, setShowRecaptchaModal] = useState(false);

    const serviceDelayed = JSON.parse(localStorage.getItem(LOCAL_STORAGE_DELAYED_SERVICE_CHANGE));

    const authBootstrapped = useAuthRedirectManager({
        auth,
        appType,
        role_list,
        service,
        setService,
        serviceDialer,
        setDialerService,
        authInfo,
        maintenanceMode
    });

    useLastVisitedTracker({ auth, appType, service });

    useEffect(() => {
        const ua = navigator.userAgent;
        const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

        const isDesktopUA = !/Mobi|Mobile/.test(ua);
        const mobileDesktopMode = isTouchDevice && isDesktopUA;

        let meta = document.querySelector('meta[name="viewport"]');

        if (!meta) {
            meta = document.createElement("meta");
            meta.name = "viewport";
            document.head.appendChild(meta);
        }

        if (mobileDesktopMode) {
            const DESKTOP_WIDTH = 1920;
            const scale = window.innerWidth / DESKTOP_WIDTH;
            meta.setAttribute(
                "content",
                `width=${DESKTOP_WIDTH}, initial-scale=${scale}`
            );
        } else {
            meta.setAttribute("content", "width=device-width, initial-scale=1");
        }

        window.dispatchEvent(new Event("resize"));
    }, []);

    useEffect(() => {
        checkCaptchaNeeded();
    }, []);

    // useEffect(() => {
    //     if (appType === APP_TYPE_ADMIN) {
    //         if (service !== serviceDelayed && serviceDelayed !== null) {
    //             const lastService = localStorage.getItem("LAST_SERVICE_BEFORE_SESSION_EXPIRED");

    //             const newService = lastService ? transformed_service_data[lastService] : serviceDelayed;
    //             setService(newService);
    //         }

    //         if (service !== null && service !== undefined) {
    //             localStorage.setItem("service", service);
    //         }
    //     }
    // }, [service]);

    const currentServiceValue = useMemo(() => {
        return appType === APP_TYPE_ADMIN ? service : serviceDialer;
    }, [appType, service, serviceDialer]);

    const prevServiceValue = usePrevious(currentServiceValue);

    useEffect(() => {
        setBodyClassNames(authInfo || {});

        const managedServiceList = getManagedServices(authInfo);

        if (auth && !maintenanceMode) {
            if (managedServiceList.length === 1) {
                const currentService = transformed_service_data[managedServiceList[0]];
                const lastService = localStorage.getItem("LAST_SERVICE_BEFORE_SESSION_EXPIRED");

                const newService = lastService ? transformed_service_data[lastService] : currentService;
                setService(newService);
            }
        }
    }, [auth, maintenanceMode, authInfo]);

    useEffect(() => {
        if (visibleRecaptcha) {
            setRecaptchaIsVisible(visibleRecaptcha);
            setShowRecaptchaModal(true);
        }
    }, [visibleRecaptcha]);

    // preload common info
    useEffect(() => {
        if (auth && !maintenanceMode && (!referenceInited || prevServiceValue !== currentServiceValue)) {
            if (accountJoinList.length > 1 || appType === APP_TYPE_ADMIN) {
                // if (currentServiceValue !== null) {
                getReferences(appType, currentServiceValue, accountJoinList);
                // }
            } else if (accountJoinList.length === 1 && currentServiceValue !== null || appType === APP_TYPE_ADMIN) {
                if (serviceDialer !== null) {
                    getReferences(appType, currentServiceValue, accountJoinList);
                }
            }
        }
    }, [auth, referenceInited, maintenanceMode, accountJoinList.length, currentServiceValue, location.pathname]);

    useEffect(() => {
        if (auth && !maintenanceMode) {
            getPermission();
        }

        if (!auth) {
            setInitedReferences(false);
        }
    }, [auth, maintenanceMode]);

    useEffect(() => {
        if (auth && !maintenanceMode) {
            if (appType === APP_TYPE_CLIENT) {
                getAccountDialerInfo();
            }
        }
    }, [auth, maintenanceMode, appType]);

    useEffect(() => {
        if (safePermissions.includes(ACCOUNT_MANAGERS_GET_INFORMATORS_API)) {
            getAccountManagersInformators();
        }
    }, [service, safePermissions]);

    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty("--color-main", service ? "var(--color-violet)" : "var(--color-info)");
        root.style.setProperty("--color-main-hover", service ? "var(--color-violet-dark)" : "var(--activate-bg)");
        root.style.setProperty("--color-sort", service ? "var(--color-sort-voice)" : "var(--color-sort-sms)");
    }, [service])

    useEffect(() => {
        if (accountJoinList.length === 1) {
            const accountService = accountJoinList[0];
            const currentService = accountService?.service;

            setDialerService(transformed_service_data[currentService]);
            localStorage.setItem(HAS_BOTH_SERVICE_VALUE, false);
        } else if (accountJoinList.length === 2) {
            localStorage.setItem(HAS_BOTH_SERVICE_VALUE, true);
            // setDialerService(true);
        }

    }, [authInfo, accountJoinList])

    const locale = lang || "en";
    const messages = locales[locale];
    const rsMessages = localesRsuite[locale] || localesRsuite.en;

    const executeRecaptcha = () => {

        if (!recaptchaIsVisible) {
            handleResolveCaptcha();
            return;
        }

        if (!recaptchaRef?.current?.execute) {
            return;
        }

        recaptchaRef.current.execute();
    }

    const resetRecaptcha = () => {

        if (!recaptchaIsVisible) {
            return;
        }

        if (!recaptchaRef?.current?.reset) {
            return;
        }

        recaptchaRef.current.reset();
    };

    const handleResolveCaptcha = (token) => {
        if (!token && recaptchaIsVisible) {
            Alert.error("Bot verification failed")
            resetRecaptcha();
            return;
        }

        if (methodRecaptcha) {
            api(methodRecaptcha, {
                ...paramsRecaptcha,
                captcha_token: token,
            })
                .then((response) => {
                    store.dispatch(storeDataOnRecaptchaComplete(methodRecaptcha, response));
                })
            // .finally(() => {
            //     resetRecaptcha();
            // });
        }
        setShowRecaptchaModal(false);
    };

    return (
        <IntlProvider locale={locale} messages={messages}>
            <CustomProvider locale={rsMessages}>
                <AlertBridge />
                <ThemeProvider theme={themeContext[appType]}>
                    <AppTypeContext.Provider value={appType}>
                        <RtlContext.Provider value={rtl}>
                            <PermissionContext.Provider value={safePermissions}>

                                {recaptchaIsVisible && <Recaptcha
                                    ref={recaptchaRef}
                                    sitekey={process.env.REACT_APP_SITE_KEY}
                                    onResolved={handleResolveCaptcha}
                                />}

                                <Switch>

                                    {maintenanceMode &&
                                        <Route path="/" component={Maintenance} />
                                    }

                                    <Route
                                        path="/login"
                                        component={Login}
                                    />
                                    <Route path="/reset-password/:token?" component={ResetPassword} />
                                    <Route path="/verify-mail/:token?" component={EmailVerification} />
                                    {!auth && appType !== APP_TYPE_ADMIN &&
                                        <Route exact path="/payment-detail/:token" component={PaymentDetailConfirm} />
                                    }
                                    {!auth &&
                                        <Redirect to={DEFAULT_NOT_AUTH_PATH} />
                                    }

                                    {auth && !authBootstrapped ? null : (
                                        appType === APP_TYPE_ADMIN
                                            ? <AdminRoutes
                                                auth={auth}
                                                service={service}
                                                authInfo={authInfo}
                                            />
                                            : <ClientRoutes
                                                auth={auth}
                                                authInfo={authInfo}
                                                dialerInfo={dialerInfo}
                                                accountJoinList={accountJoinList}
                                                executeRecaptcha={executeRecaptcha}
                                                resetRecaptcha={resetRecaptcha}
                                            />
                                    )}

                                </Switch>

                                <ModalResponseHandlerGlobalHOC />
                                <CacheBooster />

                                {showRecaptchaModal && <RecaptchaModal
                                    show={showRecaptchaModal}
                                    onConfirm={() => {

                                    }}
                                    customFooter={() => (
                                        <Button
                                            onClick={() => {
                                                executeRecaptcha();
                                                setShowRecaptchaModal(false);
                                            }}
                                            appearance="primary"
                                        >
                                            Ok
                                        </Button>
                                    )}
                                    title={"Warning"}
                                    showFooter
                                >
                                    Too many operations!
                                </RecaptchaModal>}

                                {/* <DebugFooterOverlay /> */}
                            </PermissionContext.Provider>
                        </RtlContext.Provider>
                    </AppTypeContext.Provider>
                </ThemeProvider>
            </CustomProvider>
        </IntlProvider>
    );
}

const mapState = ({ auth, roles, settings, auth_dialer, references }) => ({
    appType: auth.appType,
    authInfo: auth.authInfo,
    dialerInfo: auth_dialer.dialerInfo,
    accountJoinList: auth_dialer.accountJoinList,
    auth: auth.auth,
    lang: auth.lang,
    rtl: auth.rtl,
    role_list: roles.role_list,
    service: auth.service,
    referenceInited: references.inited,
    smsNotDeployed: references.sms_not_deployed,
    voiceNotDeployed: references.voice_not_deployed,
    serviceDialer: auth_dialer.serviceDialer,
    visibleRecaptcha: settings.visibleRecaptcha,
    methodRecaptcha: settings.methodRecaptcha,
    paramsRecaptcha: settings.paramsRecaptcha,
    permissions: auth.permissions,
    maintenanceMode: auth.maintenanceMode,
});
//
// ATX-2922
//
export default connect(mapState, {
    setService,
    getDialerTrunk,
    getReferences,
    getPermission,
    setDialerService,
    checkCaptchaNeeded,
    setInitedReferences,
    getAccountDialerInfo,
    getAccountManagersInformators,
})(App);


const RecaptchaModal = styled(CustomModal)`
    .rs-modal-header-close {
        display: none;
    }
`;

const HidenForm = styled(Form)`

`;
